import { useCallback } from 'react';

import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useQuizEditorCreateQuestionsMutation as useCreateQuestions } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/mutations/generated/createQuestions';
import { useQuizEditorRemoveQuestionsMutation as useRemoveQuestions } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/mutations/generated/removeQuestions';
import { useQuizEditorUpdateQuestionsMutation as useUpdateQuestions } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/mutations/generated/updateQuestions';
import { useUpdateQuizQuestionsMutation as useUpdateQuizQuestions } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/mutations/generated/updateQuizQuestions';
import {
  QuestionInput,
  QuizQuestionActionEnum,
  ResourceStatusEnum,
  UpdateQuestionEntity,
  UpdateQuizInput,
  UpdateQuizQuestionConfigInput,
} from '@/types/schema';

import { useQuizEditorUpdateQuizMutation } from '../../graphql/mutations/generated/updateQuiz';

interface Props {
  quizId: string;
}

const useQuizEditorMutations = ({ quizId }: Props) => {
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [createQuestions, executeCreateQuestions] = useCreateQuestions();
  const [removeQuestions, executeRemoveQuestions] = useRemoveQuestions();
  const [updateQuestions, executeUpdateQuestions] = useUpdateQuestions();
  const [updateQuiz, executeUpdateQuiz] = useQuizEditorUpdateQuizMutation();
  const [updateQuizQuestions, executeUpdateQuizQuestions] = useUpdateQuizQuestions();

  const onCreateQuestion = useCallback(
    async (questionsInput: QuestionInput[]) => {
      const response = await executeCreateQuestions({
        data: { questions: questionsInput, maintainDraftState: true, quizId },
      });

      const questions = response.data?.createQuestions?.createdQuestions;
      const quizQuestions = response.data?.createQuestions?.createdQuizQuestions;

      if (response.error) {
        showDefaultErrorSnackbar();
      }

      return { questions, quizQuestions, response };
    },
    [executeCreateQuestions, quizId, showDefaultErrorSnackbar],
  );

  const onRemoveQuestion = useCallback(
    async (questionId: string) => {
      const response = await executeRemoveQuestions({
        data: {
          quizId,
          questionConfigs: [{ questionId, action: QuizQuestionActionEnum.Remove }],
        },
      });

      if (response.error || !response.data?.addRemoveQuizQuestions?.success) {
        showDefaultErrorSnackbar();
      }

      return { response };
    },
    [executeRemoveQuestions, quizId, showDefaultErrorSnackbar],
  );

  const onUpdateQuizQuestions = useCallback(
    async (questionConfigs: UpdateQuizQuestionConfigInput[]) => {
      const response = await executeUpdateQuizQuestions({ data: { quizId, questionConfigs } });

      if (response.error || !response.data?.updateQuizQuestions?.success) {
        showDefaultErrorSnackbar();
      }

      return { response };
    },
    [executeUpdateQuizQuestions, quizId, showDefaultErrorSnackbar],
  );

  const onUpdateQuestions = useCallback(
    (questions: UpdateQuestionEntity[]) => {
      return executeUpdateQuestions({
        data: { updateQuestionEntities: questions, maintainDraftState: true },
      }).then((response) => {
        if (response.error) {
          showDefaultErrorSnackbar();
        }

        return { response };
      });
    },
    [executeUpdateQuestions, showDefaultErrorSnackbar],
  );

  const onUpdateQuiz = async (quiz: UpdateQuizInput) => {
    const response = await executeUpdateQuiz({
      data: { ...quiz, maintainDraftState: true },
      versionStatus: ResourceStatusEnum.Draft,
    });

    if (response.error) {
      showDefaultErrorSnackbar();
    }
    return { quiz: response.data?.updateQuiz.quiz, response };
  };

  return {
    fetching:
      createQuestions.fetching ||
      removeQuestions.fetching ||
      updateQuiz.fetching ||
      updateQuestions.fetching ||
      updateQuizQuestions.fetching,
    onCreateQuestion,
    onRemoveQuestion,
    onUpdateQuestions,
    onUpdateQuiz,
    onUpdateQuizQuestions,
  };
};

export type UseQuizEditorMutationsReturnType = ReturnType<typeof useQuizEditorMutations>;
export default useQuizEditorMutations;
